/**
 * TeamOverviewWorkspace — the Pulse / Flight Board workspace (manager + principal).
 *
 * Four tabs. **Flight Plan** (FlightPlanTab) is the front door and the ONLY place work is assigned:
 * RASCI was retired 2026-09-02 (docs/FLOW_RECONCILIATION_PLAN.md §0), crews are the only assignment
 * mechanism, and a queued milestone has no owner by design. The other three tabs are firm-shape,
 * not assignment: **Performance** (measured snapshots — health, load, pressure, throughput, plus
 * what the nightly scheduler did and what it could not resolve), **Efficiency** (aging work, cycle
 * time, over-budget and stalled phases) and **Anatomy** (capacity vs demand, staffing model, the
 * skill layers and the algorithm levers).
 *
 * Data: the Practice Performance snapshots + phase/milestone rollups + Team-contact capacity
 * (services/teamOverview, shaped by outline/practiceMetrics). Who is on what is read live from the
 * crew index (outline/crew.js) — never from a person's paper assignments, which no longer exist.
 *
 * Workspace recipe mirrors BillingWorkspace: open/close via `open-team`/`close-team`, `?ws=team` URL +
 * session/localStorage, docked tray-aware via `.aj-team-ws`. Gated to (hasModule('workload') ||
 * hasModule('admin')) — performance/allocation data is Manager-and-up only.
 */

import { useEffect, useRef, useState } from 'react'
import { hasModule, isSeatLoaded } from '../services/tenancy.js'
import { isEmbedded } from '../outline/state.js'
import { WorkspaceHeader, workspaceTabsWrapStyle, workspaceTabStyle } from './WorkspaceChrome.jsx'
import { ctx } from '../outline/ctx.js'
import { getTeamCapacity, getSnapshotHistory, getPhaseEfficiency, getAgingMilestones, getRecentlyPosted, getMemberSkills, saveMemberSkill, deleteMemberSkill, getRoleSkills, saveRoleSkill, deleteRoleSkill, getPracticeRoles, getDemonstratedSkills } from '../services/teamOverview.js'
import { rollupFirm, buildTrends, rollupCapacity, rollupEfficiency, modelHiring, rollupAging, rollupFever, rollupCycleTime, buildCoachContext, summarizeRun, leverMap, firmHealthScore, nightlyChurn } from '../outline/practiceMetrics.js'
import { SKILL_TAGS, personSkillSheet } from '../outline/eligibility.js'
import { crewForMilestone, crewLabel, flightsForMember, ensureCrewIndex } from '../outline/crew.js'
import { triggerBackfill, getSchedulerRunHistory, getSchedulerConfig, saveSchedulerLever } from '../services/scheduler.js'
import { getPracticeInsights } from '../services/practiceAI.js'
import { recomputeAllSkills } from '../services/skills.js'
import { getLearnedEffort } from '../services/capacity.js'
import { getReconcileItems, runReconcileAction } from '../services/reconcile.js'
import { useScrollLock } from '../hooks/useScrollLock.js'
import FlightPlanTab from './FlightPlanTab.jsx'
import ReservesTab from './ReservesTab.jsx'
import LineUpTab from './LineUpTab.jsx'
import { getLineUpShort, onLineUpShort } from '../services/onDeck.js'
import { kindMeta } from '../outline/reconcile.js'
import { isoToday, fmtDueDate, EFFORT_LEVER_KEYS, ASSIGN_EFFORT_HOURS } from '../services/work.js'
import { MetricTrendCard, HBars, Columns, InfoDot, PALETTE, Ring, Sparkline, FeverChart } from './MiniChart.jsx'
import { CoachBanner, CoachNote, CoachChat } from './PracticeCoach.jsx'
import { confirmDialog } from './Dialog.jsx'

// Section ids the AI writes a one-line note for (must match the keys rendered under each chart).
const COACH_SECTIONS = ['fever', 'load', 'pressure', 'throughput', 'movement']

// Plain-language chart explainers (success criteria: every chart must be readable without insider
// knowledge). Kept here so the dashboard and its charts stay in sync.
const HELP = {
  throughput: 'Items your team finished in the last 7 days — checklist items completed plus milestones posted. Higher is more output. Read the trend over weeks, not a single day.',
  load: 'Average effort load across the team: hours worked in the last 7 days vs each person’s normal weekly hours. About 1.0 is a normal pace, above ~1.3 is ramping hard, below ~0.8 is light. It’s a pace signal — not good or bad by itself.',
  atRisk: 'Open items that are overdue, or flagged urgent/impossible by the scheduler. Lower is better.',
  overdue: 'Open items past their due date. Lower is better.',
  loadByPerson: 'Each person’s effort load: their hours in the last 7 days vs their own weekly baseline. Red = ramping hard, blue = light, green = steady. This shows pace, not who is “best.”',
  pressure: 'How far behind schedule each project is, in working days (from the scheduler). Higher = further behind. The top bar is your biggest schedule problem right now.',
  thruByPerson: 'Items each person finished in the last 7 days. A momentum check, not a ranking — people on planning-heavy work finish fewer discrete items.',
  movement: 'How many milestones the nightly scheduler moved later (pushed) vs earlier (pulled) on each run. Lots of pushing means deadlines are slipping; pulling means the team is catching up.',
}

function loadColorFor(r) {
  if (r == null) return '#cdd8dc'
  if (r > 1.3) return PALETTE.red
  if (r < 0.8) return PALETTE.blue
  return PALETTE.green
}

const STORE_KEY = 'aj:team:ws'

function readStored() {
  try {
    const ws = new URLSearchParams(window.location.search).get('ws')
    if (ws === 'team') return { active: true }
    if (ws !== null) return null
    // A peek/split iframe must never auto-restore a workspace from storage shared with the
    // parent tab — see ScheduleIntelWorkspace.jsx's own comment on this same check.
    if (isEmbedded()) return null
    const t = sessionStorage.getItem(STORE_KEY)
    if (t) return JSON.parse(t)
    return JSON.parse(localStorage.getItem(STORE_KEY) || 'null')
  } catch (e) { return null }
}

export default function TeamOverviewWorkspace() {
  const stored = readStored()
  const [active, setActive] = useState(!!(stored && stored.active))
  const [seatReady, setSeatReady] = useState(isSeatLoaded())
  // The Line-up is the tower's main daily tool, so the board opens on it (Matt, 2026-09-27).
  // The Line-up was retired (Matt, 2026-10-26: "Get rid of line it up"); the board opens on what is in the air.
  const [tab, setTab] = useState('today')        // 'lineup' | 'today' | 'tower' | 'drum' | 'flow' | 'firm' | 'efficiency' | 'anatomy'
  const [lineUp, setLineUp] = useState(null)       // { short, ready, needWork } — the tab's red count
  const [roster, setRoster] = useState(null)      // [{ memberId, name, capacity:{practiceRoles} }] — the team, not their load
  const [firm, setFirm] = useState(null)          // { rollup, trends, capacity, snapshotDate, days }
  const [eff, setEff] = useState(null)            // { overBudget, stalled, phases }
  const [hireCap, setHireCap] = useState(null)    // rollupCapacity result for the hiring model
  const [hireCount, setHireCount] = useState(1)
  const [hireHrs, setHireHrs] = useState(40)
  const [loading, setLoading] = useState(false)
  const [firmLoading, setFirmLoading] = useState(false)
  const [effLoading, setEffLoading] = useState(false)
  const [hireLoading, setHireLoading] = useState(false)
  const [error, setError] = useState(null)
  const [expanded, setExpanded] = useState({})    // memberId -> bool
  const [backfillMsg, setBackfillMsg] = useState(null)
  const [coach, setCoach] = useState(null)         // { headline, coaching, notes } | null

  // Lock background scroll while the workspace is open — the CrmWorkspace pattern. Missing here
  // was the actual bug behind "the outline's scrollbar shows to the right of the page": .aj-team-ws
  // is position:fixed over the outline, but fixed positioning alone does not stop the page
  // underneath from scrolling — wheel/keyboard input (and the visible native scrollbar track)
  // still moved the hidden outline behind it. Gated on `active` rather than mount/unmount, since
  // this component stays mounted and toggles `active` instead of unmounting.
  //
  // Reference-counted (useScrollLock, hooks/useScrollLock.js) rather than a raw
  // document.body.style.overflow set/restore: GanttModal and PostMilestoneModal can both open ON
  // TOP of this workspace (from the Flight Tracker tab) and now hold their own lock too — a naive
  // version here would have its cleanup fire on THEIR unmount timing in some renders, or theirs
  // would restore '' while this workspace is still open, either way reopening the exact "outline
  // scrolls behind it" bug this effect exists to prevent.
  useScrollLock(active)
  const [coachLoading, setCoachLoading] = useState(false)
  const [coachErr, setCoachErr] = useState(null)
  const [coachCtx, setCoachCtx] = useState(null)   // the JSON context handed to the chat
  const [skills, setSkills] = useState(null)       // manual override layer: { memberId: [{skill,proficiency}] }
  const [roleSkills, setRoleSkills] = useState(null) // role → expected skills (the heavy signal)
  const [practiceRoles, setPracticeRoles] = useState(null) // { roles:[{id,name,hierarchy,lane}], byId }
  const [demonstrated, setDemonstrated] = useState(null)   // learned skills: { memberId: [{skill,level}] }
  const [recomputeMsg, setRecomputeMsg] = useState(null)   // skill-recompute status line (Anatomy)
  const [recomputing, setRecomputing] = useState(false)
  const [learnedEffort, setLearnedEffort] = useState(null) // learned per-type effort actuals (capacity feed)
  const [reconcile, setReconcile] = useState(null)         // scheduler-untouchable stuck items (Reconcile lane)
  const [overnight, setOvernight] = useState(null)         // summarized latest scheduler run (what moved + why)
  const [churn, setChurn] = useState(null)                 // nightly churn trend (plan stability)
  const [levers, setLevers] = useState(null)               // tunable scheduler/workload config (7b)
  const [chatOpen, setChatOpen] = useState(false)
  const [chatSeed, setChatSeed] = useState('')
  const loadedRef = useRef(false)
  const firmLoadedRef = useRef(false)
  const effLoadedRef = useRef(false)
  const hireLoadedRef = useRef(false)

  useEffect(() => {
    function onOpen() { setActive(true) }
    function onClose() { setActive(false) }
    // A resolution (e.g. marking an item reactive) changed the data — reload what's loaded.
    function onResolved() {
      loadedRef.current = false; firmLoadedRef.current = false
      if (tab === 'firm') loadFirm()
      load()
      loadedRef.current = true; if (tab === 'firm') firmLoadedRef.current = true
    }
    window.addEventListener('open-team', onOpen)
    window.addEventListener('close-team', onClose)
    window.addEventListener('resolve-applied', onResolved)
    return () => { window.removeEventListener('open-team', onOpen); window.removeEventListener('close-team', onClose); window.removeEventListener('resolve-applied', onResolved) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab])

  useEffect(() => {
    try {
      if (active) { const j = JSON.stringify({ active: true }); sessionStorage.setItem(STORE_KEY, j); localStorage.setItem(STORE_KEY, j) }
      else { sessionStorage.removeItem(STORE_KEY); localStorage.removeItem(STORE_KEY) }
    } catch (e) { /* ignore */ }
  }, [active])

  useEffect(() => {
    const url = new URL(window.location)
    if (active) { url.searchParams.set('ws', 'team'); document.title = 'Alder James Flight Board' }
    else if (url.searchParams.get('ws') === 'team') url.searchParams.delete('ws')
    window.history.replaceState(null, '', url)
  }, [active])

  useEffect(() => {
    if (seatReady) return
    const id = setInterval(() => { if (isSeatLoaded()) { setSeatReady(true); clearInterval(id) } }, 100)
    return () => clearInterval(id)
  }, [seatReady])

  useEffect(() => {
    if (active && seatReady && !(hasModule('workload') || hasModule('admin'))) setActive(false)
  }, [active, seatReady])

  const allowed = () => active && seatReady && (hasModule('workload') || hasModule('admin'))

  // Crew labels resolve asynchronously — outline/crew.js fires `outline-render` when the index
  // lands (and again after any crew write). Repaint so a row that read "not crewed" while the
  // index was still loading corrects itself instead of staying wrong until the next interaction.
  const [, repaint] = useState(0)
  useEffect(() => {
    if (!active) return
    const onRender = () => repaint(n => n + 1)
    window.addEventListener('outline-render', onRender)
    return () => window.removeEventListener('outline-render', onRender)
  }, [active])

  // Firm rings/trends — needed by the Performance tab AND the Today cockpit.
  useEffect(() => {
    if (!allowed() || tab !== 'firm' || firmLoadedRef.current) return
    firmLoadedRef.current = true
    loadFirm()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, seatReady, tab])

  // Efficiency data — needed by the Efficiency tab, the Today feed, and the Performance fever chart.
  useEffect(() => {
    if (!allowed() || !(tab === 'efficiency' || tab === 'firm') || effLoadedRef.current) return
    effLoadedRef.current = true
    loadEff()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, seatReady, tab])

  // Anatomy tab — needs the capacity rollup (reuse the Firm tab's if already loaded).
  useEffect(() => {
    if (!allowed() || tab !== 'anatomy' || hireLoadedRef.current) return
    hireLoadedRef.current = true
    if (firm && firm.capacity) setHireCap(firm.capacity)
    else loadHiring()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, seatReady, tab])

  // Roster + skills + levers + scheduler observability — load once on open (Anatomy and Performance
  // both read pieces of it). The crew index comes with it: every "who is on this" label in the
  // workspace reads outline/crew.js, which resolves once and re-renders on `outline-render`.
  useEffect(() => {
    if (!allowed() || loadedRef.current) return
    loadedRef.current = true
    ensureCrewIndex()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, seatReady])

  async function loadFirm() {
    setFirmLoading(true); setError(null)
    try {
      const [hist, contacts, lvRaw] = await Promise.all([getSnapshotHistory(90), getTeamCapacity(), getSchedulerConfig().catch(() => null)])
      if (lvRaw) setLevers(lvRaw)
      const lv = leverMap(lvRaw)
      const dates = [...new Set((hist.members || []).map(m => m.snapshot_date))].sort()
      const latest = dates[dates.length - 1] || null
      const latestMembers = (hist.members || []).filter(m => m.snapshot_date === latest)
      const latestProjects = (hist.projects || []).filter(p => p.snapshot_date === latest)
      const firmObj = { rollup: rollupFirm(latestMembers, latestProjects, lv), trends: buildTrends(hist), capacity: rollupCapacity(contacts, lv), health: firmHealthScore(hist, lv), snapshotDate: latest, days: dates.length }
      setFirm(firmObj)
      genCoach(firmObj)
    } catch (e) {
      setError(e.message || 'Could not load firm metrics.')
    } finally { setFirmLoading(false) }
  }

  // Write the plain-language firm read (Bevel-style coaching) off the same rollups the charts use.
  // Best-effort: charts render with or without it. Per-person figures come from the snapshot rollup
  // (load / 7d hours / completions) — measured effort, not assignments.
  async function genCoach(firmObj) {
    const fever = eff && eff.phasesRaw ? rollupFever(eff.phasesRaw, (firmObj.rollup && firmObj.rollup.projectPressure) || []) : []
    const context = buildCoachContext({ firm: firmObj, eff, fever })
    setCoachCtx(context); setCoach(null); setCoachErr(null); setCoachLoading(true)
    try {
      setCoach(await getPracticeInsights(context, COACH_SECTIONS))
    } catch (e) {
      setCoachErr(e.message || 'AI is unavailable.')
    } finally { setCoachLoading(false) }
  }

  function openChat(seed) { setChatSeed(seed || ''); setChatOpen(true) }

  // Skills editor (Anatomy) — write the eligibility layer + update local state optimistically.
  async function saveSkill(memberId, memberName, skill, proficiency) {
    const { skill: row } = await saveMemberSkill(memberId, memberName, skill, proficiency)
    setSkills(s => {
      const next = { ...(s || {}) }
      const arr = (next[memberId] || []).filter(x => x.skill !== skill)
      arr.push({ id: row.id, skill: row.skill, proficiency: row.proficiency })
      next[memberId] = arr
      return next
    })
  }
  async function removeSkill(memberId, id) {
    await deleteMemberSkill(id)
    setSkills(s => { const next = { ...(s || {}) }; next[memberId] = (next[memberId] || []).filter(x => x.id !== id); return next })
  }
  async function saveRole(role, skill, proficiency) {
    const { roleSkill: row } = await saveRoleSkill(role, skill, proficiency)
    setRoleSkills(s => {
      const next = { ...(s || {}) }
      const arr = (next[role] || []).filter(x => x.skill !== skill)
      arr.push({ id: row.id, skill: row.skill, proficiency: row.proficiency })
      next[role] = arr
      return next
    })
  }
  async function removeRole(role, id) {
    await deleteRoleSkill(id)
    setRoleSkills(s => { const next = { ...(s || {}) }; next[role] = (next[role] || []).filter(x => x.id !== id); return next })
  }
  async function saveLever(key, value) {
    const { lever } = await saveSchedulerLever(key, value)
    setLevers(ls => (ls || []).map(l => l.key === key ? { ...l, value: lever.value } : l))
  }
  // Capacity feed switch: default vs learned effort. Persisting is enough — the Capacity & effort
  // panel reads the lever straight off `levers`, which saveLever updates in place.
  async function setCapacityFeed(value) {
    await saveLever('use_learned_effort', value)
  }
  // Reconcile lane: 'open' navigates; everything else is a pre-canned write shortcut. After a write,
  // refresh the lane (the scheduler re-equalizes on its next run around the reconciled fact).
  async function doReconcile(item, action) {
    if (action.id === 'open') { go({ zoomType: item.entityType, entityId: item.entityId, projectId: item.projectId }); return }
    if (action.id === 'complete') { window.dispatchEvent(new CustomEvent('open-closeout-modal', { detail: { msId: item.entityId, name: item.title, projectId: item.projectId } })); return }
    if (!(await confirmDialog({ title: action.label + '?', message: `${action.label} — “${item.title}”${item.project ? ` (${item.project})` : ''}?`, confirmLabel: action.label }))) return
    await runReconcileAction(item, action.id)
    getReconcileItems().then(setReconcile).catch(() => {})
  }

  async function loadHiring() {
    setHireLoading(true); setError(null)
    try {
      const contacts = await getTeamCapacity()
      setHireCap(rollupCapacity(contacts))
    } catch (e) {
      setError(e.message || 'Could not load capacity data.')
    } finally { setHireLoading(false) }
  }

  async function loadEff() {
    setEffLoading(true); setError(null)
    try {
      // No "nobody owns this" pass any more: queued work has no owner by design (Decision 3), so an
      // ownerless milestone is the normal state, not a risk to list.
      const [phases, agingRaw, postedRaw] = await Promise.all([getPhaseEfficiency(), getAgingMilestones(), getRecentlyPosted()])
      setEff({ ...rollupEfficiency(phases, isoToday()), aging: rollupAging(agingRaw, isoToday()), cycle: rollupCycleTime(postedRaw), phasesRaw: phases })
    } catch (e) {
      setError(e.message || 'Could not load efficiency data.')
    } finally { setEffLoading(false) }
  }

  async function load() {
    setLoading(true); setError(null)
    try {
      // The team roster is required; every skill layer is best-effort (a missing one just thins the
      // character sheet). Nothing here is an assignment — who is flying what comes from the crew index.
      const [contacts, memberSkills, roleSkillMap, roles, demo, lvRaw, learned] = await Promise.all([
        getTeamCapacity(),
        getMemberSkills().catch(() => null),   // manual override layer — best-effort
        getRoleSkills().catch(() => null),     // role → skills (heavy signal) — best-effort
        getPracticeRoles().catch(() => null),  // the firm's real roles (resolve link IDs → names)
        getDemonstratedSkills().catch(() => null), // learned-from-work skills — best-effort
        getSchedulerConfig().catch(() => null),    // tunable levers (7b)
        getLearnedEffort().catch(() => null),      // learned per-type effort actuals (capacity feed)
      ])
      getReconcileItems().then(setReconcile).catch(() => setReconcile([]))  // scheduler-untouchable stuck items
      if (memberSkills) setSkills(memberSkills)
      if (roleSkillMap) setRoleSkills(roleSkillMap)
      if (roles) setPracticeRoles(roles)
      if (demo) setDemonstrated(demo)
      if (lvRaw) setLevers(lvRaw)
      if (learned) setLearnedEffort(learned)
      setRoster(buildRoster(contacts, roles ? roles.byId : null))
      // Overnight report + nightly churn trend (best-effort, observability).
      getSchedulerRunHistory(30).then(runs => { const p = runs && runs[0] && runs[0].payload; if (p) setOvernight(summarizeRun(p)); setChurn(nightlyChurn(runs)) }).catch(() => {})
    } catch (e) {
      setError(e.message || 'Could not load the team roster.')
    } finally { setLoading(false) }
  }

  function refresh() {
    if (tab === 'firm') { firmLoadedRef.current = false; loadFirm(); firmLoadedRef.current = true }
    else if (tab === 'efficiency') { effLoadedRef.current = false; loadEff(); effLoadedRef.current = true }
    else if (tab === 'anatomy') { hireLoadedRef.current = false; loadHiring(); loadedRef.current = false; setExpanded({}); load(); hireLoadedRef.current = true; loadedRef.current = true }
    // In the Air and Reserves re-read themselves live (lib/useLiveRefresh.js); this button just asks them to now.
    else window.dispatchEvent(new CustomEvent('flight-board-refresh'))
  }

  async function backfill() {
    setBackfillMsg('Starting backfill…')
    try {
      await triggerBackfill(90)
      setBackfillMsg('Backfill started — it runs on GitHub Actions (~1–2 min). Refresh shortly to see history fill in.')
    } catch (e) {
      setBackfillMsg('Could not start backfill: ' + (e.message || 'error'))
    }
  }
  // Recompute every person's demonstrated skills from approved attributed time (recompute-from-source,
  // re-weighted by the current Skill levers), persist, then refresh the learned layer so the character
  // sheets update in place. Levels stay empty until time entries are attributed + approved.
  async function recompute() {
    setRecomputing(true); setRecomputeMsg('Recomputing skills from approved time…')
    try {
      const r = await recomputeAllSkills()
      const demo = await getDemonstratedSkills().catch(() => null)
      if (demo) setDemonstrated(demo)
      setRecomputeMsg(r.written
        ? `Recomputed: ${r.written} skill levels across ${r.members} ${r.members === 1 ? 'person' : 'people'}.`
        : 'No demonstrated skills yet — attribute and approve time entries first.')
    } catch (e) {
      setRecomputeMsg('Could not recompute: ' + (e.message || 'error'))
    } finally { setRecomputing(false) }
  }
  function toggle(id) { setExpanded(e => ({ ...e, [id]: !e[id] })) }

  function go(item) {
    // newTab: opens elsewhere and leaves THIS tab exactly as it is — so the workspace must
    // NOT close here. closeAllWorkspaces() is for the normal case, where this tab is about to
    // navigate away and the Flight Tracker (or whatever's open) would otherwise be stranded
    // mid-navigation.
    if (item.zoomType && item.entityId && ctx.zoomToEntity && item.newTab) {
      ctx.zoomToEntity(item.zoomType, item.entityId, item.projectId, true); return
    }
    // Dispatch the shared close event (not a local setActive(false)) so SiteNav's own
    // "Pulse" highlight — driven only by open-team/close-team — updates in lockstep.
    ctx.closeAllWorkspaces()
    if (item.zoomType && item.entityId && ctx.zoomToEntity) { ctx.zoomToEntity(item.zoomType, item.entityId, item.projectId); return }
    if (item.projectId && ctx.zoomToProject) { ctx.zoomToProject(item.projectId); if (item.navRoot) setTimeout(() => ctx.zoomToRoot(item.navRoot), 0) }
  }

  // The Line-up tab's red count (same reading as the Flight Board menu item).
  useEffect(() => {
    if (!active) return undefined
    const off = onLineUpShort(setLineUp)
    getLineUpShort().then(setLineUp).catch(() => {})
    return off
  }, [active])

  if (!active) return null

  const restricted = isSeatLoaded() && !(hasModule('workload') || hasModule('admin'))

  return (
    <div className="aj-team-ws" style={S.panel}>
      <WorkspaceHeader
        title="Flight Board"
        tabs={!restricted && (
          <div style={workspaceTabsWrapStyle}>
            <button style={workspaceTabStyle(tab !== 'reserves')} onClick={() => setTab('today')}>In the Air</button>
            <button style={workspaceTabStyle(tab === 'reserves')} onClick={() => setTab('reserves')}>Buffers</button>
          </div>
        )}
        actions={(
          <>
            <button style={S.refresh} onClick={refresh} disabled={loading || firmLoading}>{(loading || firmLoading) ? 'Loading…' : 'Refresh'}</button>
          </>
        )}
      />

      {restricted ? (
        <div style={S.restricted}>This is a manager view. Contact your administrator.</div>
      ) : tab === 'reserves' ? (
        // Reserves (Matt, 2026-10-27): the fuel reserve chart + every project's buffer, from Build Ready.
        <div style={S.content}><div style={S.inner}>
          <ReservesTab />
        </div></div>
      ) : (
        // In the Air: the Flight Plan, the only place work is assigned (FLOW_RECONCILIATION_PLAN §0, Decision 2).
        // The other tabs (Ops Director Triage, Tower, Drum, Flow, Performance, Efficiency, Anatomy) were removed
        // 2026-10-27 at Matt's word; the Operations Director's morning lockout (FocusWalkGate) is unchanged.
        <div style={S.content}><div style={S.inner}>
          <FlightPlanTab />
        </div></div>
      )}

      {chatOpen && (
        <CoachChat context={coachCtx || {}} title="Ask about the firm" seed={chatSeed} onClose={() => setChatOpen(false)} />
      )}
    </div>
  )
}

// 'flow' — the constraint monitor (Flow Reconciliation Step 4, FlowTab.jsx): where time pools,
// seats in use, throughput, inventory, flow time, tower latency. Self-loading, read-only.
// 'drum' — days of direction each person owes, week by week, read off the schedule (sheet 2, docs/DRUM.md).
const DETAIL_TABS = [{ id: 'tower', label: 'Tower' }, { id: 'drum', label: 'Drum' }, { id: 'flow', label: 'Flow' }, { id: 'firm', label: 'Performance' }, { id: 'efficiency', label: 'Efficiency' }, { id: 'anatomy', label: 'Anatomy' }]

/**
 * The team roster from the Team contacts — WHO the firm is, not what anyone is carrying. Practice
 * Roles resolve from link ids to names so the skill sheet can read a person's role baseline. Shaped
 * as { memberId, name, capacity: { practiceRoles } } because that is the shape eligibility.js's
 * personSkillSheet reads.
 */
function buildRoster(contacts, roleNameById) {
  const first = v => (Array.isArray(v) ? (v[0] ?? null) : (v ?? null))
  return (contacts || []).map(c => {
    const f = c.fields || {}
    const midRaw = first(f['Record ID (from Members)'])
    const memberId = midRaw ? String(midRaw).split(',')[0] : null
    const roleIds = Array.isArray(f['Practice Roles']) ? f['Practice Roles'] : (f['Practice Roles'] ? [f['Practice Roles']] : [])
    const roleNames = roleNameById ? roleIds.map(id => roleNameById[id]).filter(Boolean) : []
    return {
      memberId,
      name: [f['First Name'], f['Last Name']].filter(Boolean).join(' ') || f['Name'] || '',
      capacity: { practiceRoles: roleNames.length ? roleNames.join(', ') : (first(f['Alder James Role']) || null) },
    }
  }).filter(p => p.memberId && p.name).sort((a, b) => a.name.localeCompare(b.name))
}

/**
 * Reconcile lane — the items the scheduler can't auto-resolve (pinned / reactive / blocked), forced in
 * front of a human with pre-canned one-tap shortcuts. Grouped by kind; the registry (outline/reconcile)
 * drives the look + actions, so new kinds appear here automatically.
 */
function ReconcileLane({ items, onAct }) {
  if (!items || items.length === 0) return null
  const byKind = {}
  items.forEach(it => (byKind[it.kind] = byKind[it.kind] || []).push(it))
  return (
    <div style={S.decWrap}>
      <div style={S.decHead}>Reconcile <span style={S.todaySub}>(the scheduler can’t move these — decide, and it re-levels around your call)</span></div>
      {Object.keys(byKind).map(kind => {
        const km = kindMeta(kind)
        return (
          <div key={kind} style={{ marginBottom: 10 }}>
            {km.blurb && <div style={S.reconBlurb}>{km.blurb}</div>}
            {byKind[kind].map(it => (
              <div key={it.id} style={S.reconCard}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={S.decTagRow}><span style={{ ...S.decTag, background: km.bg, color: km.text }}>{km.tag}</span></div>
                  <div style={S.decTitle}>{it.title}</div>
                  <div style={S.decProj}>{[it.project, it.why].filter(Boolean).join(' · ')}</div>
                </div>
                <div style={S.decActions}>
                  {km.actions.map((a, i) => (
                    <button key={a.id} style={i === 0 ? S.decApprove : S.decGhost} onClick={() => onAct(it, a)}>{a.label}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )
      })}
    </div>
  )
}

/**
 * Overnight report (trust layer — observability): what last night's scheduler run moved and WHY,
 * read from the run payload (summarizeRun). Collapsed to a one-liner; expands to the firm stats + the
 * milestones that moved with derived reasons (chronic slippage flagged). The machine's work, visible.
 */
function OvernightReport({ report, churn }) {
  const [open, setOpen] = useState(false)
  if (!report) return null
  const s = report.stats
  const churnColor = !churn ? PALETTE.mute : churn.direction === 'improving' ? PALETTE.green : churn.direction === 'rising' ? PALETTE.red : PALETTE.mute
  return (
    <div style={S.overnight}>
      <button style={S.overnightHead} onClick={() => setOpen(o => !o)}>
        <span style={S.decChevron}>{open ? '▾' : '▸'}</span>
        <span style={S.overnightTitle}>Overnight: {s.scheduled} scheduled · {s.pushed} pushed · {s.pulled} pulled{s.blocked ? ` · ${s.blocked} blocked` : ''}</span>
        {report.chronic.length > 0 && <span style={S.overnightFlag}>{report.chronic.length} chronic</span>}
      </button>
      {open && (
        <div style={S.overnightBody}>
          {churn && churn.series.length > 1 && (
            <div style={S.churnRow}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={S.churnHead}>
                  <span style={S.churnNum}>{churn.latest}</span>
                  <span style={S.churnLbl}>milestones moved last night</span>
                  {churn.delta != null && churn.delta !== 0 && <span style={{ ...S.churnDelta, color: churnColor }}>{churn.delta < 0 ? '▼' : '▲'} {Math.abs(churn.delta)} vs recent avg</span>}
                </div>
                <div style={S.churnSub}>Nightly churn — lower means the plan is holding and the team can service it. {churn.direction === 'improving' ? 'Settling down.' : churn.direction === 'rising' ? 'Climbing — the plan is moving more.' : ''}</div>
              </div>
              <Sparkline points={churn.series} color={churnColor} width={150} height={34} />
            </div>
          )}
          <div style={S.overnightStats}>{s.scheduled} scheduled · {s.pushed} pushed out · {s.pulled} pulled in · {s.cascade} cascaded · {s.blocked} waiting on a predecessor · {s.skippedFuture} too far out to schedule yet{s.pinned ? ` · ${s.pinned} pinned` : ''}</div>
          {report.movers.slice(0, 10).map((m, i) => (
            <div key={i} style={S.overnightRow}>
              <span style={{ ...S.sevDot, background: m.status === 'DEFERRED' ? PALETTE.amber : m.pushCount >= 8 ? PALETTE.red : m.pulled ? PALETTE.green : PALETTE.mute }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={S.feedLabel}>{m.name} <span style={S.overnightProj}>{m.project}{m.who && m.who !== 'Unassigned' ? ` · ${m.who}` : ''}</span></div>
                <div style={S.feedSub}>{m.reason}</div>
              </div>
            </div>
          ))}
          {report.movers.length > 10 && <div style={S.decMore}>+{report.movers.length - 10} more moved</div>}
        </div>
      )}
    </div>
  )
}

/**
 * Capacity & effort (Anatomy) — the per-type effort assumptions that drive leveling, shown as
 * DEFAULT vs LEARNED side by side, with a switch for which feed the scheduler uses. Learned is the
 * average hours per item from approved attributed time; it only takes over once a type has enough
 * samples (so a thin average never prematurely overrides a sane default). Monitors the gap over time.
 */
function CapacityPanel({ levers, learned, onToggle }) {
  const [open, setOpen] = useState(false)
  const lv = leverMap(levers)
  const useLearned = !!lv.use_learned_effort
  const minN = Number(lv.learned_min_sample) || 4
  const rows = Object.entries(EFFORT_LEVER_KEYS).map(([k, type]) => {
    const def = Number.isFinite(Number(lv[k])) ? Number(lv[k]) : ASSIGN_EFFORT_HOURS[type]
    const L = learned && learned[type]
    const learnedH = L && L.hoursPerItem != null ? L.hoursPerItem : null
    const items = L ? L.items : 0
    const trusted = useLearned && learnedH != null && items >= minN
    return { type, def, learnedH, items, trusted }
  })
  return (
    <div style={S.rolePanel}>
      <button style={S.rolePanelHead} onClick={() => setOpen(o => !o)}>
        <span style={S.tri}>{open ? '▾' : '▸'}</span>
        Capacity & effort <span style={S.skillsHint}>— how long each work type is assumed to take</span>
      </button>
      {open && (
        <div style={S.rolePanelBody}>
          <div style={S.capFeedRow}>
            <span style={S.capFeedLbl}>Scheduler effort feed</span>
            <div style={S.capToggle}>
              <button style={useLearned ? S.capOpt : S.capOptOn} onClick={() => onToggle(0)}>Default</button>
              <button style={useLearned ? S.capOptOn : S.capOpt} onClick={() => onToggle(1)}>Learned</button>
            </div>
            <span style={S.capFeedNote}>{useLearned ? `Using learned where ≥ ${minN} samples, default otherwise` : 'Using default assumptions'}</span>
          </div>
          <div style={{ ...S.realRow, ...S.realHeadRow }}>
            <span style={S.capType}>Work type</span>
            <span style={S.capCol}>Default</span>
            <span style={S.capCol}>Learned</span>
            <span style={S.capCol}>Samples</span>
            <span style={S.capCol}>In use</span>
          </div>
          {rows.map(r => (
            <div key={r.type} style={S.realRow}>
              <span style={S.capType}>{r.type}</span>
              <span style={S.capCol}>{r.def}h</span>
              <span style={{ ...S.capCol, color: r.learnedH != null ? 'var(--aj-text)' : '#cdd8dc' }}>{r.learnedH != null ? `${r.learnedH}h` : '—'}</span>
              <span style={S.capCol}>{r.items || '—'}</span>
              <span style={{ ...S.capCol, fontWeight: 600, color: r.trusted ? PALETTE.green : 'var(--aj-text-muted)' }}>{r.trusted ? 'learned' : 'default'}</span>
            </div>
          ))}
          <div style={S.realFoot}>Defaults are our starting assumptions; learned is the average hours per item from approved, attributed time. The scheduler uses whichever feed is selected — learned only once a type has ≥ {minN} samples. Learned fills in as the team attributes time to work items.</div>
        </div>
      )}
    </div>
  )
}

/**
 * Role → expected skills editor (Anatomy). The heavy, low-upkeep eligibility signal: define each
 * role's skills once and everyone in that role inherits them. Roles are discovered from the team's
 * Practice Roles, so the firm just keeps roles assigned (which it already does).
 */
function RoleSkillsPanel({ roles, roleSkills, onSave, onDelete }) {
  const [open, setOpen] = useState(false)
  if (!roles || !roles.length) return null
  return (
    <div style={S.rolePanel}>
      <button style={S.rolePanelHead} onClick={() => setOpen(o => !o)}>
        <span style={S.tri}>{open ? '▾' : '▸'}</span>
        Role skills <span style={S.skillsHint}>— define once per role; everyone in the role inherits it</span>
      </button>
      {open && (
        <div style={S.rolePanelBody}>
          {roles.map(role => (
            <RoleSkillRow key={role.id} role={role} skills={(roleSkills && roleSkills[role.name]) || []} onSave={onSave} onDelete={onDelete} />
          ))}
        </div>
      )}
    </div>
  )
}

function RoleSkillRow({ role, skills, onSave, onDelete }) {
  return (
    <div style={S.roleRow}>
      <div style={S.roleName}>{role.name}{role.lane ? <span style={S.roleLane}>{role.lane}</span> : null}</div>
      <SkillDotGrid levels={levelMap(skills)}
        onSet={(skill, level) => onSave(role.name, skill, level)}
        onClear={(skill, id) => onDelete(role.name, id)} />
    </div>
  )
}

/**
 * The tunable LEVERS behind the algorithm (trust 7b). Grouped, editable; this is "see and adjust the
 * levers." Source of truth is scheduler_config; consumers read it (incrementally wired). Scheduler-
 * applied levers note that they take effect on the next nightly run.
 */
function LeverPanel({ levers, onSave }) {
  const [open, setOpen] = useState(false)
  if (!levers || !levers.length) return null
  const groups = {}
  levers.forEach(l => { (groups[l.grp || 'Other'] = groups[l.grp || 'Other'] || []).push(l) })
  return (
    <div style={S.rolePanel}>
      <button style={S.rolePanelHead} onClick={() => setOpen(o => !o)}>
        <span style={S.tri}>{open ? '▾' : '▸'}</span>
        Algorithm levers <span style={S.skillsHint}>— the constants behind scheduling + workload; adjust as you learn what's real</span>
      </button>
      {open && (
        <div style={S.rolePanelBody}>
          {Object.keys(groups).map(grp => (
            <div key={grp} style={S.leverGroup}>
              <div style={S.leverGroupHd}>{grp}</div>
              {groups[grp].map(l => <LeverRow key={l.key} lever={l} onSave={onSave} />)}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function LeverRow({ lever, onSave }) {
  const [val, setVal] = useState(String(lever.value))
  const [busy, setBusy] = useState(false)
  const dirty = String(lever.value) !== val
  async function commit() {
    if (!dirty || busy) return
    const v = Number(val)
    if (!Number.isFinite(v)) { setVal(String(lever.value)); return }
    setBusy(true)
    try { await onSave(lever.key, v) } catch (e) { setVal(String(lever.value)) }
    finally { setBusy(false) }
  }
  return (
    <div style={S.leverRow}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={S.leverLabel}>{lever.label} {lever.applies_to === 'scheduler' && <span style={S.leverApplies} title="Takes effect on the next nightly run">nightly</span>}</div>
        <div style={S.leverDesc}>{lever.description}</div>
      </div>
      <input style={S.leverInput} type="number" step="any" value={val}
        onChange={e => setVal(e.target.value)} onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur() }} />
      <span style={S.leverUnit}>{lever.unit}</span>
      <span style={S.leverDefault}>{dirty ? (busy ? '…' : 'enter↵') : (Number(lever.value) !== Number(lever.default_value) ? `def ${lever.default_value}` : '')}</span>
    </div>
  )
}

/**
 * Firm Health over time — the cardio-style "are we improving?" score. A 0–100 composite trended
 * weekly with its direction in plain language + the four components driving it. Honestly guards thin
 * history. Sits at the top of Performance.
 */
function FirmHealthCard({ health }) {
  if (!health || health.score == null) return null
  const dir = health.direction
  const dirColor = dir === 'improving' ? PALETTE.green : dir === 'declining' ? PALETTE.red : PALETTE.mute
  const dirWord = dir === 'improving' ? 'Improving' : dir === 'declining' ? 'Declining' : 'Holding steady'
  const c = health.components || {}
  const comps = [['Schedule risk', c.scheduleRisk], ['Load balance', c.loadBalance], ['Stability', c.stability], ['Throughput', c.throughput]]
  return (
    <div style={S.healthWrap}>
      <InfoDot text={['A composite 0–100 read of firm health, trended weekly — the "are we getting better" number. Blends schedule risk (overdue/at-risk share), load balance (everyone near a healthy pace), schedule stability (low churn), and throughput. It is a directional index: watch the week-over-week trend, not the absolute number. Components are each 0–100, higher = healthier.']} />
      <div style={S.healthTop}>
        <Ring frac={health.score / 100} color={dirColor} centerText={String(health.score)} label="Firm Health" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={S.healthDirRow}>
            <span style={{ ...S.healthChip, color: dirColor, background: dirColor + '18' }}>{dir === 'improving' ? '▲' : dir === 'declining' ? '▼' : '◆'} {dirWord}</span>
            {health.delta != null && health.delta !== 0 && <span style={S.healthDelta}>{health.delta > 0 ? '+' : ''}{health.delta} over {health.weeksOfData} weeks</span>}
          </div>
          <div style={S.healthSub}>{health.sparse
            ? `Building — ${health.weeksOfData} of ~8 weeks of history. The trend sharpens as daily snapshots accrue.`
            : 'Composite of schedule risk, load balance, stability, and throughput — week over week.'}</div>
          {health.series.length > 1 && <Sparkline points={health.series} color={dirColor} width={280} height={38} />}
        </div>
      </div>
      {!health.sparse && (
        <div style={S.healthComps}>
          {comps.map(([k, v]) => (
            <div key={k} style={S.healthComp}>
              <div style={{ ...S.healthCompV, color: v == null ? '#cdd8dc' : v >= 70 ? PALETTE.green : v >= 45 ? PALETTE.amber : PALETTE.red }}>{v == null ? '—' : v}</div>
              <div style={S.healthCompL}>{k}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function FirmDashboard({ firm, eff, onBackfill, backfillMsg, overnight, churn, reconcile, onReconcile, coach, coachLoading, coachErr, onAsk, onRetryCoach }) {
  const fever = eff && eff.phasesRaw ? rollupFever(eff.phasesRaw, (firm.rollup && firm.rollup.projectPressure) || []) : []
  const r = firm.rollup, t = firm.trends
  const sparse = firm.days < 2
  const notes = (coach && coach.notes) || {}
  return (
    <>
      {/* Bevel-style read: the AI distills the whole view into one plain-language take + a chat. */}
      <CoachBanner headline={coach && coach.headline} coaching={coach && coach.coaching}
        loading={coachLoading} error={coachErr} onAsk={onAsk} onRetry={onRetryCoach} />

      <FirmHealthCard health={firm.health} />

      {/* What the machine did overnight, and the few things it cannot resolve on its own. Both
          moved here when the Flight Plan took over the front door — they are observability, not
          assignment, so they belong with the firm's measured picture. */}
      <OvernightReport report={overnight} churn={churn} />

      <ReconcileLane items={reconcile} onAct={onReconcile} />

      <div style={S.firmHead}>
        <span style={S.firmAsOf}>How the firm is trending · measured as of {firm.snapshotDate ? fmtDueDate(firm.snapshotDate) : '—'} · {firm.days} day{firm.days === 1 ? '' : 's'} of history</span>
      </div>

      {/* Composite scores — big number + delta + hoverable trend (the Bevel score cards) */}
      <div style={S.cards}>
        <MetricTrendCard title="Throughput · 7d" points={t.throughput} color={PALETTE.green} higherIsBetter unit="done" info={HELP.throughput} />
        <MetricTrendCard title="Avg Load" points={t.avgLoad} color={PALETTE.purple} higherIsBetter={null} fmt={v => Number(v).toFixed(2)} hint="≈1.0 is steady · history builds daily" info={HELP.load} />
        <MetricTrendCard title="At-risk items" points={t.atRisk} color={PALETTE.amber} higherIsBetter={false} info={HELP.atRisk} />
        <MetricTrendCard title="Overdue items" points={t.overdue} color={PALETTE.red} higherIsBetter={false} info={HELP.overdue} />
      </div>

      {sparse && (
        <div style={S.backfillRow}>
          <span style={S.backfillTxt}>Charts are showing a single day. Backfill ~90 days of effort + throughput history (reconstructed from time entries) to see real trends now.</span>
          <button style={S.backfillBtn} onClick={onBackfill}>Backfill history</button>
        </div>
      )}
      {backfillMsg && <div style={S.backfillMsg}>{backfillMsg}</div>}

      {/* Portfolio fever chart — every project's schedule risk at a glance */}
      {fever.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <FeverChart data={fever} info={['Each dot is an active project: how far along it is (left→right) vs how many working-days behind schedule it is (bottom→top). Dots in the red band are slipping faster than they’re progressing — your real schedule risks. Hover a dot for the project. (“Behind” = the scheduler’s pressure; no contracted dates are involved.)']} />
          <CoachNote text={notes.fever} loading={coachLoading} onAsk={onAsk} />
        </div>
      )}

      {/* Distributions — who carries what, today */}
      <div style={S.dist}>
        <div style={S.distCell}>
          <HBars title="Load by person (7d hours ÷ baseline)" info={HELP.loadByPerson}
            data={r.loadByPerson.map(p => ({ name: p.name, value: p.value, sub: p.hours7d != null ? p.hours7d + 'h' : '', color: loadColorFor(p.value) }))}
            fmt={v => v == null ? '—' : Number(v).toFixed(2)} emptyText="No measured load yet" />
          <CoachNote text={notes.load} loading={coachLoading} onAsk={onAsk} />
        </div>
        <div style={S.distCell}>
          <HBars title="Project pressure (working-days behind)" info={HELP.pressure}
            data={r.projectPressure.slice(0, 8).map(p => ({ name: p.name, value: p.value, sub: p.overdue ? p.overdue + ' overdue' : '', color: p.overdue ? PALETTE.red : PALETTE.amber }))}
            fmt={v => Number(v).toFixed(0)} emptyText="Nothing behind schedule" />
          <CoachNote text={notes.pressure} loading={coachLoading} onAsk={onAsk} />
        </div>
        <div style={S.distCell}>
          <HBars title="Throughput by person (7d)" info={HELP.thruByPerson}
            data={r.throughputByPerson.map(p => ({ name: p.name, value: p.value, color: PALETTE.green }))}
            emptyText="No completions recorded yet" />
          <CoachNote text={notes.throughput} loading={coachLoading} onAsk={onAsk} />
        </div>
        <div style={S.distCell}>
          <Columns title="Schedule movement (nightly)" info={HELP.movement} data={t.movement}
            series={[{ key: 'pushed', label: 'pushed out', color: PALETTE.red }, { key: 'pulled', label: 'pulled in', color: PALETTE.green }]} />
          <CoachNote text={notes.movement} loading={coachLoading} onAsk={onAsk} />
        </div>
      </div>
    </>
  )
}

function HiringDashboard({ cap, count, hrs, setCount, setHrs }) {
  const m = modelHiring(cap, { hires: count, daysPerWeek: hrs / 8 })
  const VERDICT = {
    fine:  { label: 'Capacity is adequate', color: '#1D9E75', note: 'The gap is small — resequencing or a short push likely closes it without hiring.' },
    watch: { label: 'Worth watching', color: '#b8540a', note: 'A real but moderate gap. Watch the trend; consider contract help before a permanent hire.' },
    hire:  { label: 'Added capacity warranted', color: '#c0392b', note: 'A sustained gap of this size genuinely points to adding people.' },
  }
  const v = VERDICT[m.verdict]
  return (
    <>
      <div style={S.firmHead}>
        <span style={S.firmAsOf}>Staffing model — committed work vs what current staffing can deliver on time</span>
      </div>

      <div style={S.capPanel}>
        <InfoDot text={'The gap is the firm’s “over-deadline days” — committed person-days the scheduler can’t fit before their deadlines at current staffing. This model shows how fast added capacity would clear it. It assumes demand holds steady and the new person’s time goes to the backlog; it does not model onboarding ramp-up or new incoming work, so treat the weeks as a floor.'} />
        <div style={S.capHeadline}>
          {m.gapDays > 0
            ? <>The team is committed to <strong>{m.committedDays} person-days</strong> of work, of which <strong style={{ color: '#c0392b' }}>{m.gapDays} can’t be delivered on time</strong> at current staffing — concentrated in {m.overCommitted.length} {m.overCommitted.length === 1 ? 'person' : 'people'}.</>
            : <>Current staffing can deliver all <strong>{m.committedDays} committed person-days</strong> on time. No capacity gap.</>}
        </div>
        <div style={{ ...S.hireVerdict, color: v.color }}>{v.label}</div>
        <div style={S.hireNote}>{v.note}</div>
      </div>

      {/* What-if controls */}
      <div style={S.hireControls}>
        <div style={S.hireCtlGroup}>
          <span style={S.hireCtlLabel}>Add</span>
          {[1, 2, 3].map(n => <button key={n} style={count === n ? S.segOn : S.seg} onClick={() => setCount(n)}>{n}</button>)}
          <span style={S.hireCtlLabel}>{count === 1 ? 'person' : 'people'}</span>
        </div>
        <div style={S.hireCtlGroup}>
          <span style={S.hireCtlLabel}>at</span>
          {[20, 32, 40].map(h => <button key={h} style={hrs === h ? S.segOn : S.seg} onClick={() => setHrs(h)}>{h}h</button>)}
          <span style={S.hireCtlLabel}>/ week</span>
        </div>
      </div>

      {/* Result */}
      <div style={S.capPanel}>
        <InfoDot text={'Weeks to clear = the gap ÷ the new weekly capacity you’re adding (people × days/week, where a day ≈ 8 hours). A floor, not a promise — onboarding and new work will extend it.'} />
        {m.gapDays > 0 ? (
          <div style={S.capHeadline}>
            Adding <strong>{count} {count === 1 ? 'person' : 'people'} at {hrs}h/week</strong> adds <strong>{m.addedPerWeek} person-days/week</strong> of capacity — enough to clear the {m.gapDays}-day backlog in about <strong style={{ color: '#1D9E75' }}>{m.weeksToClear} week{m.weeksToClear === 1 ? '' : 's'}</strong>, then hold the line.
          </div>
        ) : (
          <div style={S.capHeadline}>No gap to close right now. Added capacity would go to pulling deadlines in or absorbing new work.</div>
        )}
      </div>

      {m.overCommitted.length > 0 && (
        <HBars title="Who the added capacity relieves first (over-deadline days today)" info={'The people currently carrying work that can’t hit its deadline. New capacity should be pointed here first.'}
          data={m.overCommitted.map(p => ({ name: p.name, value: p.infeasible, sub: 'of ' + p.committed + 'd', color: p.status === 'over' ? '#c0392b' : '#b8540a' }))}
          fmt={x => Number(x).toFixed(1) + 'd'} />
      )}
    </>
  )
}

function EfficiencyDashboard({ eff }) {
  const aging = eff.aging || []
  return (
    <>
      <div style={S.firmHead}>
        <span style={S.firmAsOf}>Where work is getting stuck or running over budget — across {eff.phases} active phases</span>
      </div>

      {/* Aging WIP — oldest open work (Kanban work-item age) */}
      <div style={S.effPanel}>
        <InfoDot text={['Work-item age (a Lean/Kanban flow metric): how long each open milestone has been started but not finished. Long age is the earliest sign of drift — it catches stuck work that due-dates miss, because something can be "not overdue" yet have sat untouched for weeks. Oldest first.']} />
        <div style={S.effTitle}>Aging work · oldest open milestones</div>
        {aging.length === 0 ? <div style={S.effEmpty}>No aging data.</div>
          : aging.slice(0, 12).map((a, i) => {
            // Crew, or nothing: queued work has no owner by design, so an ageing row with no crew
            // reads "not in flight" — a fact about the board, not a missing assignment.
            const crew = crewLabel(crewForMilestone(a.id))
            return (
            <div key={i} style={S.effRow}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={S.effName}>{a.name} <span style={S.effPhase}>· {a.project}</span></div>
                <div style={S.effSub}>{crew || 'not in flight'}{a.due ? ' · due ' + fmtDueDate(a.due) : ''}</div>
              </div>
              <div style={S.effMetric}>
                <div style={{ ...S.effBig, color: a.age > 60 ? '#c0392b' : a.age > 30 ? '#b8540a' : '#5a7884' }}>{a.age}d</div>
                <div style={S.effDelta}>open</div>
              </div>
            </div>
            )
          })}
      </div>

      {/* Cycle time — start → posted (backfilled) */}
      {eff.cycle && eff.cycle.count > 0 && (
        <div style={S.effPanel}>
          <InfoDot text={['How long milestones actually take from their start date to being posted — cycle time. Backfilled from existing dates (no waiting on new data). Median is the typical milestone; P90 is the slow tail; the list is the slowest recent ones, worth a look. As more post, this trends.']} />
          <div style={S.effTitle}>Cycle time · start → posted ({eff.cycle.count} recent)</div>
          <div style={S.cycleStats}>
            <span><span style={S.cycleNum}>{eff.cycle.median}d</span> median</span>
            <span><span style={S.cycleNum}>{eff.cycle.p90}d</span> 90th percentile</span>
          </div>
          {eff.cycle.slowest.map((c, i) => (
            <div key={i} style={S.effRow}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={S.effName}>{c.name} <span style={S.effPhase}>· {c.project}</span></div>
              </div>
              <div style={{ ...S.effBig, color: c.days > (eff.cycle.p90 || 9999) ? '#c0392b' : '#5a7884' }}>{c.days}d</div>
            </div>
          ))}
        </div>
      )}

      {/* Over budget — spending faster than progress */}
      <div style={S.effPanel}>
        <InfoDot text={'Phases that have used more hours than budgeted, or are burning hours faster than they’re progressing. “Used” = hours spent ÷ hours budgeted; compare it to “done” (percent complete). If used is well above done, the phase is running hot. These are where fee erodes — investigate scope, rework, or estimate.'} />
        <div style={S.effTitle}>Over budget · {eff.overBudget.length}</div>
        {eff.overBudget.length === 0 ? <div style={S.effEmpty}>No phases over budget. Nice.</div>
          : eff.overBudget.slice(0, 12).map((r, i) => {
            const usedPct = Math.round(r.pctUsed * 100), donePct = Math.round(r.pctComplete * 100)
            return (
              <div key={i} style={S.effRow}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={S.effName}>{r.project} <span style={S.effPhase}>· {r.phase}</span></div>
                  <div style={S.effSub}>{r.expended}h of {r.budget}h budget · {donePct}% complete</div>
                </div>
                <div style={S.effMetric}>
                  <div style={{ ...S.effBig, color: r.overageHrs > 0 ? '#c0392b' : '#b8540a' }}>{usedPct}%<span style={S.effUnit}> used</span></div>
                  <div style={S.effDelta}>{r.overageHrs > 0 ? `+${r.overageHrs}h over` : `${r.aheadPts}pts ahead of pace`}</div>
                </div>
              </div>
            )
          })}
      </div>

      {/* Stalled — open work, no recent activity */}
      <div style={S.effPanel}>
        <InfoDot text={'Phases that still have open milestones but no time logged recently (14+ days) — or never. Work that’s assigned but untouched. Usually a blocked dependency, a waiting-on-client item, or something that fell off the radar. The longer idle, the more it’s worth a nudge.'} />
        <div style={S.effTitle}>Stalled work · {eff.stalled.length}</div>
        {eff.stalled.length === 0 ? <div style={S.effEmpty}>Nothing stalled — all open phases have recent activity.</div>
          : eff.stalled.slice(0, 12).map((r, i) => (
            <div key={i} style={S.effRow}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={S.effName}>{r.project} <span style={S.effPhase}>· {r.phase}</span></div>
                <div style={S.effSub}>{r.openMs} open milestone{r.openMs === 1 ? '' : 's'} · {r.expended}h logged so far</div>
              </div>
              <div style={S.effMetric}>
                <div style={{ ...S.effBig, color: '#b8540a' }}>{r.idle == null ? 'never' : r.idle + 'd'}</div>
                <div style={S.effDelta}>{r.idle == null ? 'no time logged' : 'since last entry'}</div>
              </div>
            </div>
          ))}
      </div>
    </>
  )
}

function initials(name) {
  return (name || '?').split(/\s+/).filter(Boolean).map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?'
}

/**
 * One person on the Anatomy roster. Deliberately NOT a workload row: the firm keeps no per-person
 * book of assignments any more, so this answers "who is this, what are they qualified for, and what
 * are they flying right now." The flights come live from the crew index (outline/crew.js) — the same
 * source the Flight Plan writes to, so this row can never disagree with the board. Expanding shows
 * the skill sheet and the manual override editor.
 */
function TeamMemberRow({ p, open, onToggle, skills, onSaveSkill, onDeleteSkill, skillCtx }) {
  const [editSkills, setEditSkills] = useState(false)
  const flights = flightsForMember(p.memberId)
  const roles = (p.capacity && p.capacity.practiceRoles) || ''
  return (
    <div style={S.person}>
      <button style={S.personRow} onClick={onToggle}
        onMouseEnter={e => { e.currentTarget.style.background = 'var(--aj-bg-subtle)' }}
        onMouseLeave={e => { e.currentTarget.style.background = 'none' }}>
        <span style={S.tri}>{open ? '▾' : '▸'}</span>
        <span style={S.avatar}>{initials(p.name)}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={S.pName}>{p.name}</div>
          <div style={S.pSub}>{roles || 'no practice role set'}</div>
        </div>

        <div style={S.chips}>
          {flights.length === 0
            ? <span style={{ ...S.chip, color: '#8a9ba3', background: 'rgba(7,39,45,0.06)' }}>no flight</span>
            : flights.map(f => (
                <span key={f.flightId} style={{ ...S.chip, color: PALETTE.teal, background: PALETTE.teal + '18' }}
                  title={[f.flightNumber, f.milestoneName || (f.kind === 'squadron' ? 'squadron' : ''), f.projectName].filter(Boolean).join(' · ')}>
                  {f.flightNumber}
                </span>
              ))}
        </div>
      </button>

      {open && (
        <div style={S.items}>
          {skillCtx && <SkillSheet sheet={personSkillSheet(p, skillCtx)} />}
          {onSaveSkill && (
            <div style={S.skillEditWrap}>
              <button style={S.skillEditToggle} onClick={() => setEditSkills(v => !v)}>
                {editSkills ? '▾' : '▸'} Manual skill overrides {(skills && skills.length) ? `(${skills.length})` : ''}
              </button>
              {editSkills && <SkillsEditor memberId={p.memberId} memberName={p.name} practiceRoles={roles} skills={skills} onSave={onSaveSkill} onDelete={onDeleteSkill} />}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * A 0–5 dot rating. Click a dot to set that level; click the current level again to clear (0).
 * Hover previews the level. No dropdowns, no labels — just dots.
 */
function DotRating({ value = 0, onChange, color = '#16B5A6' }) {
  const [hover, setHover] = useState(0)
  const shown = hover || value
  return (
    <span style={S.dots} onMouseLeave={() => setHover(0)}>
      {[1, 2, 3, 4, 5].map(n => (
        <button key={n} type="button" title={`${n} of 5`}
          style={{ ...S.dot, background: n <= shown ? color : 'transparent', borderColor: n <= shown ? color : 'var(--aj-border-mid)' }}
          onMouseEnter={() => setHover(n)}
          onClick={() => onChange(n === value ? 0 : n)} />
      ))}
    </span>
  )
}

/**
 * Skill matrix: every skill tag with a dot rating. Setting a level saves; clearing (0) removes.
 * `levels` is a map skill → { id, level }. Shared by the per-person and per-role editors.
 */
function SkillDotGrid({ levels, onSet, onClear }) {
  return (
    <div style={S.dotGrid}>
      {SKILL_TAGS.map(tag => {
        const cur = levels[tag]
        return (
          <div key={tag} style={S.dotRow}>
            <span style={S.dotLabel}>{tag}</span>
            <DotRating value={cur ? cur.level : 0} onChange={n => (n > 0 ? onSet(tag, n) : (cur && onClear(tag, cur.id)))} />
          </div>
        )
      })}
    </div>
  )
}

function levelMap(rows) {
  const m = {}
  ;(rows || []).forEach(r => { m[r.skill] = { id: r.id, level: r.proficiency } })
  return m
}

function sheetColor(l) { return l >= 4 ? '#1FB57A' : l >= 2.5 ? '#16B5A6' : l >= 1 ? '#3D7DFF' : '#8a9ba3' }

/**
 * The RPG-style skill sheet — a person's current level per skill (0–5), blended from role + learned +
 * manual, shown as bars with the source. Read-only; this is "the points." Manager-gated (Anatomy).
 */
function SkillSheet({ sheet }) {
  const demo = (sheet && sheet.demonstrated) || []
  const roleOnly = (sheet && sheet.roleOnly) || []
  if (!demo.length && !roleOnly.length) return <div style={S.skillsWrap}><div style={S.skillsNone}>No skills yet — assign a role, or run Recompute skills to derive levels from logged time.</div></div>
  return (
    <div style={S.skillsWrap}>
      <div style={S.skillsHead}>Skill sheet <span style={S.skillsHint}>— demonstrated from logged work · tick marks the role baseline</span></div>
      {demo.length ? (
        <div style={S.sheetGrid}>
          {demo.map(s => (
            <div key={s.skill} style={S.sheetRow} title={`${s.skill}: demonstrated ${s.level.toFixed(1)}${s.role ? ` · role baseline ${s.role.toFixed(1)}` : ''}`}>
              <span style={S.sheetLabel}>{s.skill}</span>
              <div style={S.sheetTrack}>
                <div style={{ ...S.sheetFill, width: (s.level / 5 * 100) + '%', background: sheetColor(s.level) }} />
                {s.role > 0 && <div style={{ ...S.sheetTick, left: (s.role / 5 * 100) + '%' }} />}
              </div>
              <span style={S.sheetLvl}>{s.level.toFixed(1)}</span>
            </div>
          ))}
        </div>
      ) : <div style={S.skillsNone}>No demonstrated skills yet — run Recompute skills to derive levels from logged time.</div>}
      {roleOnly.length > 0 && (
        <div style={S.roleBaseline}><span style={S.roleBaselineLbl}>Role baseline also covers</span> {roleOnly.map(r => r.skill).join(' · ')}</div>
      )}
    </div>
  )
}

/**
 * Per-person eligibility skills (Anatomy) — the rare override layer over the role baseline.
 * Role skills already count automatically; this refines a specific person.
 */
function SkillsEditor({ memberId, memberName, practiceRoles, skills, onSave, onDelete }) {
  return (
    <div style={S.skillsWrap}>
      <div style={S.skillsHead}>Skills for {memberName} <span style={S.skillsHint}>— overrides the role baseline{practiceRoles ? ` (role: ${practiceRoles})` : ''}</span></div>
      <SkillDotGrid levels={levelMap(skills)}
        onSet={(skill, level) => onSave(memberId, memberName, skill, level)}
        onClear={(skill, id) => onDelete(memberId, id)} />
    </div>
  )
}

const S = {
  shortBadge: { marginLeft: 6, fontFamily: 'DM Mono, monospace', fontSize: 10, padding: '1px 6px', borderRadius: 20, background: 'rgba(192,57,43,0.12)', color: '#c0392b', border: '0.5px solid rgba(192,57,43,0.4)' },
  panel: { display: 'flex', flexDirection: 'column' },
  refresh: { fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text)', background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: '4px 12px', cursor: 'pointer' },

  // Reconcile lane cards (Performance)
  decTagRow: { display: 'flex', alignItems: 'center', gap: 8 },
  decTag: { fontFamily: '"Montserrat",sans-serif', fontSize: 9.5, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', padding: '2px 9px', borderRadius: 20 },
  decTitle: { fontFamily: '"DM Sans",sans-serif', fontSize: 14.5, fontWeight: 600, color: 'var(--aj-text)', marginTop: 8 },
  decProj: { fontFamily: '"DM Mono",monospace', fontSize: 11, color: '#8a9ba3', marginTop: 2 },
  decActions: { display: 'flex', flexDirection: 'column', gap: 7, flexShrink: 0 },
  decGhost: { fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)', background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 7, padding: '7px 14px', cursor: 'pointer' },
  reconBlurb: { fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text-muted)', lineHeight: 1.5, margin: '0 2px 8px' },
  reconCard: { display: 'flex', gap: 14, alignItems: 'flex-start', background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 12, marginBottom: 8, padding: '13px 15px', boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 2px 10px rgba(0,0,0,0.05)' },

  todaySub: { color: 'var(--aj-text-muted)', fontSize: 12.5 },
  feedLabel: { fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  feedSub: { fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-muted)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },

  firmHead: { marginBottom: 12 },
  firmAsOf: { fontFamily: '"DM Mono",monospace', fontSize: 10.5, color: 'var(--aj-text-muted)' },
  anatomyRosterHead: { fontFamily: '"Montserrat",sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--aj-text)', margin: '18px 2px 10px' },
  rosterIntro: { fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text-muted)', lineHeight: 1.5, margin: '0 2px 12px' },
  capPanel: { position: 'relative', background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 10, padding: '14px 16px', marginBottom: 14 },
  capHeadline: { fontFamily: '"DM Sans",sans-serif', fontSize: 13.5, color: 'var(--aj-text)', lineHeight: 1.5, marginBottom: 12, paddingRight: 24 },

  recomputeRow: { display: 'flex', alignItems: 'center', gap: 12, background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 10, padding: '12px 14px', marginTop: 14 },
  recomputeTitle: { fontFamily: '"Montserrat",sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--aj-text)', marginBottom: 3 },
  recomputeSub: { fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text-muted)', lineHeight: 1.45 },

  // Capacity & effort panel (default vs learned + feed switch)
  capFeedRow: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 },
  capFeedLbl: { fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, color: 'var(--aj-text)' },
  capToggle: { display: 'inline-flex', background: 'var(--aj-border)', borderRadius: 7, padding: 2 },
  capOpt: { fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: '#5a7884', background: 'none', border: 'none', borderRadius: 5, padding: '4px 14px', cursor: 'pointer' },
  capOptOn: { fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, fontWeight: 600, color: 'var(--aj-text)', background: 'var(--aj-bg-elevated)', border: 'none', borderRadius: 5, padding: '4px 14px', cursor: 'pointer', boxShadow: '0 1px 2px rgba(0,0,0,0.12)' },
  capFeedNote: { fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-muted)' },
  capType: { fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)', flex: 1, minWidth: 0 },
  capCol: { fontFamily: '"DM Mono",monospace', fontSize: 11.5, color: 'var(--aj-text-mid)', width: 72, flexShrink: 0, textAlign: 'right' },

  cards: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(330px, 1fr))', gap: 12, marginBottom: 14 },
  dist: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12 },
  distCell: { display: 'flex', flexDirection: 'column' },

  // Firm Health card
  healthWrap: { position: 'relative', background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 14, padding: '16px 18px', marginBottom: 16, boxShadow: '0 1px 2px rgba(0,0,0,0.04)' },
  healthTop: { display: 'flex', alignItems: 'center', gap: 18 },
  healthDirRow: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 5 },
  healthChip: { fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, fontWeight: 700, borderRadius: 20, padding: '3px 11px' },
  healthDelta: { fontFamily: '"DM Mono",monospace', fontSize: 11, color: 'var(--aj-text-muted)' },
  healthSub: { fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-mid)', lineHeight: 1.45, marginBottom: 8, maxWidth: 560 },
  healthComps: { display: 'flex', gap: 24, marginTop: 14, paddingTop: 12, borderTop: '0.5px solid var(--aj-border)' },
  healthComp: { textAlign: 'center' },
  healthCompV: { fontFamily: '"DM Mono",monospace', fontSize: 18, fontWeight: 600, lineHeight: 1 },
  healthCompL: { fontFamily: '"DM Sans",sans-serif', fontSize: 10.5, color: '#8a9ba3', marginTop: 3 },
  overnight: { background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 10, marginBottom: 14, overflow: 'hidden' },
  overnightHead: { display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '10px 14px', cursor: 'pointer' },
  overnightTitle: { fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, fontWeight: 600, color: 'var(--aj-text)', flex: 1, minWidth: 0 },
  overnightFlag: { fontFamily: '"DM Mono",monospace', fontSize: 10, color: '#F2555A', background: '#F2555A18', borderRadius: 8, padding: '1px 7px', flexShrink: 0 },
  overnightBody: { padding: '0 14px 12px 32px' },
  overnightStats: { fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: '#5a7884', marginBottom: 10, lineHeight: 1.5 },
  overnightRow: { display: 'flex', alignItems: 'flex-start', gap: 9, padding: '5px 0' },
  overnightProj: { fontFamily: '"DM Sans",sans-serif', fontSize: 10.5, fontWeight: 400, color: '#8a9ba3' },
  churnRow: { display: 'flex', alignItems: 'center', gap: 14, padding: '4px 0 12px', marginBottom: 10, borderBottom: '0.5px solid var(--aj-border)' },
  churnHead: { display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' },
  churnNum: { fontFamily: '"DM Mono",monospace', fontSize: 22, fontWeight: 600, color: 'var(--aj-text)', lineHeight: 1 },
  churnLbl: { fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: '#5a7884' },
  churnDelta: { fontFamily: '"DM Mono",monospace', fontSize: 10.5, fontWeight: 600 },
  churnSub: { fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-muted)', lineHeight: 1.4, marginTop: 3 },
  decWrap: { marginTop: 16 },
  decHead: { fontFamily: '"Montserrat",sans-serif', fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--aj-text)', marginBottom: 8 },
  decMore: { fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: '#8a9ba3', marginTop: 6, fontStyle: 'italic' },
  sevDot: { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },
  decChevron: { fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: '#8a9ba3', flexShrink: 0 },
  decApprove: { fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, fontWeight: 600, color: '#fff', background: '#07272D', border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer' },

  // Skills editor (Anatomy → per person)
  skillsWrap: { padding: '8px 16px 12px', borderBottom: '0.5px solid var(--aj-border)' },
  skillsHead: { fontFamily: '"Montserrat",sans-serif', fontSize: 9.5, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', marginBottom: 7 },
  skillsHint: { fontFamily: '"DM Sans",sans-serif', fontSize: 10, fontWeight: 400, letterSpacing: 0, textTransform: 'none', color: '#9fb2ba' },
  skillsNone: { fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: '#9fb2ba', fontStyle: 'italic' },

  // Dot rating + skill matrix (replaces the dropdowns)
  dotGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(195px, 1fr))', columnGap: 32, rowGap: 2, marginTop: 4 },
  dotRow: { display: 'flex', alignItems: 'center', gap: 9, padding: '4px 0' },
  dotLabel: { fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text-mid)', width: 84, flexShrink: 0 },
  dots: { display: 'inline-flex', gap: 3 },
  dot: { width: 12, height: 12, borderRadius: '50%', border: '1.2px solid', padding: 0, cursor: 'pointer', transition: 'background 0.1s' },

  // RPG skill sheet (read-only "points")
  sheetGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', columnGap: 28, rowGap: 3, marginTop: 4 },
  sheetRow: { display: 'flex', alignItems: 'center', gap: 8 },
  sheetLabel: { fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text-mid)', width: 96, flexShrink: 0 },
  sheetTrack: { position: 'relative', flex: 1, height: 6, borderRadius: 3, background: 'var(--aj-border)' },
  sheetFill: { height: '100%', borderRadius: 3 },
  sheetTick: { position: 'absolute', top: -2, bottom: -2, width: 2, background: 'var(--aj-text-mid)', borderRadius: 1, transform: 'translateX(-50%)' },
  sheetLvl: { fontFamily: '"DM Mono",monospace', fontSize: 10.5, color: 'var(--aj-text)', width: 22, textAlign: 'right', flexShrink: 0 },
  roleBaseline: { fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#9fb2ba', marginTop: 10, lineHeight: 1.5 },
  roleBaselineLbl: { fontWeight: 600, color: 'var(--aj-text-muted)' },
  skillEditWrap: { padding: '0 0 6px' },
  skillEditToggle: { fontFamily: '"DM Sans",sans-serif', fontSize: 10.5, color: '#9fb2ba', background: 'none', border: 'none', padding: '4px 16px', cursor: 'pointer', letterSpacing: '0.02em' },

  // Role skills panel (Anatomy)
  rolePanel: { background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 10, marginBottom: 12, overflow: 'hidden' },
  rolePanelHead: { display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '12px 16px', cursor: 'pointer', fontFamily: '"Montserrat",sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--aj-text)' },
  rolePanelBody: { padding: '0 16px 8px' },
  roleRow: { padding: '10px 0', borderTop: '0.5px solid var(--aj-border)' },
  roleName: { fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, fontWeight: 600, color: 'var(--aj-text)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 },
  roleLane: { fontFamily: '"DM Mono",monospace', fontSize: 9.5, fontWeight: 400, color: 'var(--aj-text-muted)', background: 'var(--aj-bg-muted)', borderRadius: 8, padding: '1px 7px', textTransform: 'uppercase', letterSpacing: '0.04em' },

  // Shared table rows (Capacity & effort)
  realRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0', borderTop: '0.5px solid var(--aj-border)' },
  realHeadRow: { borderTop: 'none', paddingBottom: 6 },
  realFoot: { fontFamily: '"DM Sans",sans-serif', fontSize: 10.5, color: '#8a9ba3', lineHeight: 1.45, marginTop: 10 },

  // Lever control panel (trust 7b)
  leverGroup: { padding: '8px 0', borderTop: '0.5px solid var(--aj-border)' },
  leverGroupHd: { fontFamily: '"Montserrat",sans-serif', fontSize: 9.5, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', marginBottom: 4 },
  leverRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0' },
  leverLabel: { fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, fontWeight: 600, color: 'var(--aj-text)', display: 'flex', alignItems: 'center', gap: 6 },
  leverApplies: { fontFamily: '"DM Mono",monospace', fontSize: 8.5, color: '#7a6fb0', background: '#7B6CF618', borderRadius: 6, padding: '1px 5px', textTransform: 'uppercase', letterSpacing: '0.04em' },
  leverDesc: { fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-muted)', lineHeight: 1.4, marginTop: 1 },
  leverInput: { width: 64, fontFamily: '"DM Mono",monospace', fontSize: 12.5, color: 'var(--aj-text)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 6, padding: '5px 7px', textAlign: 'right', flexShrink: 0 },
  leverUnit: { fontFamily: '"DM Mono",monospace', fontSize: 10, color: '#8a9ba3', width: 56, flexShrink: 0 },
  leverDefault: { fontFamily: '"DM Mono",monospace', fontSize: 9.5, color: '#9fb2ba', width: 52, textAlign: 'right', flexShrink: 0 },
  backfillRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', marginBottom: 14, background: 'rgba(26,111,168,0.10)', border: '0.5px solid rgba(26,111,168,0.3)', borderRadius: 10 },
  backfillTxt: { flex: 1, fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: '#07272D', lineHeight: 1.45 },
  backfillBtn: { flexShrink: 0, fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, fontWeight: 600, color: '#fff', background: '#1a6fa8', border: 'none', borderRadius: 6, padding: '7px 14px', cursor: 'pointer' },
  backfillMsg: { fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: '#1a6fa8', marginBottom: 14, padding: '0 2px' },

  effPanel: { position: 'relative', background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 10, padding: '12px 16px', marginBottom: 12 },
  effTitle: { fontFamily: '"Montserrat",sans-serif', fontSize: 10, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#5a7884', marginBottom: 8, paddingRight: 24 },
  effEmpty: { fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-muted)', fontStyle: 'italic', padding: '4px 0' },
  effRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '9px 0', borderTop: '0.5px solid var(--aj-border)' },
  effName: { fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  effPhase: { fontWeight: 400, color: 'var(--aj-text-muted)' },
  effSub: { fontFamily: '"DM Sans",sans-serif', fontSize: 10.5, color: 'var(--aj-text-muted)', marginTop: 1 },
  effMetric: { flexShrink: 0, textAlign: 'right', minWidth: 96 },
  effBig: { fontFamily: '"DM Mono",monospace', fontSize: 15, fontWeight: 600, lineHeight: 1 },
  effUnit: { fontSize: 9.5, color: 'var(--aj-text-muted)', fontWeight: 400 },
  effDelta: { fontFamily: '"DM Mono",monospace', fontSize: 9.5, color: 'var(--aj-text-muted)', marginTop: 2 },
  cycleStats: { display: 'flex', gap: 24, marginBottom: 8, fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-muted)' },
  cycleNum: { fontFamily: '"DM Mono",monospace', fontSize: 18, fontWeight: 600, color: 'var(--aj-text)', marginRight: 4 },

  hireVerdict: { fontFamily: '"Montserrat",sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 4 },
  hireNote: { fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-mid)', lineHeight: 1.45 },
  hireControls: { display: 'flex', flexWrap: 'wrap', gap: 18, alignItems: 'center', padding: '10px 16px', marginBottom: 12, background: 'var(--aj-bg-subtle)', borderRadius: 10 },
  hireCtlGroup: { display: 'flex', alignItems: 'center', gap: 6 },
  hireCtlLabel: { fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: '#5a7884' },
  seg: { fontFamily: '"DM Mono",monospace', fontSize: 12, color: 'var(--aj-text)', background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' },
  segOn: { fontFamily: '"DM Mono",monospace', fontSize: 12, fontWeight: 700, color: '#fff', background: '#07272D', border: '0.5px solid #07272D', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' },
  // Full-width scroll container so the scrollbar sits at the screen's outer edge…
  content: { flex: 1, overflow: 'auto' },
  // …while the content itself stays width-limited and centered.
  // Was maxWidth 1000, which left most of a wide screen empty and squeezed the Flight Tracker's
  // hill into a fraction of the space it wants. These are dense operational surfaces — charts,
  // tables, a drag target — not prose, so the reading-measure argument for a narrow column does
  // not apply. Capped generously rather than unbounded so it stays sane on an ultrawide.
  inner: { maxWidth: 1760, width: '100%', margin: '0 auto', padding: '16px 26px' },
  restricted: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: 'var(--aj-text-mid)' },
  filler: { padding: 40, textAlign: 'center', fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: 'var(--aj-text-muted)' },




  person: { background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 10, marginBottom: 8, overflow: 'hidden' },
  personRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer' },
  tri: { fontFamily: 'monospace', fontSize: 11, color: 'var(--aj-text-muted)', width: 10, flexShrink: 0 },
  avatar: { width: 30, height: 30, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: '"DM Mono",monospace', fontSize: 11, fontWeight: 700, background: 'var(--aj-border)', color: 'var(--aj-text)' },
  pName: { fontFamily: '"DM Sans",sans-serif', fontSize: 13.5, fontWeight: 600, color: 'var(--aj-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  pSub: { fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-muted)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  chips: { display: 'flex', gap: 5, flexShrink: 0, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end', width: 210 },
  chip: { fontFamily: '"DM Mono",monospace', fontSize: 9.5, fontWeight: 600, padding: '2px 7px', borderRadius: 20, whiteSpace: 'nowrap' },

  items: { borderTop: '0.5px solid var(--aj-border)', background: 'var(--aj-bg-subtle)' },
}
